// @ts-check
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUpdateFavorite } from 'modules/queries/account/user/stateData/useUpdate';
import api from 'modules/queries/api';
import useInvalidateAssets from 'modules/queries/invalidateQueries/useInvalidateAssets';
import toast from '@liquidweb-dev/undercurrent/toast';
import useSnackbarError from 'utility/hooks/useSnackbarError';

const useServerUpdate = () => {
	const snackbarError = useSnackbarError();
	const invalidateAssets = useInvalidateAssets();

	return useMutation({
		mutationFn:
			/** @param {import('club-sauce/public/server/index.raw').LWApiPublicServerUpdateParamsRawI} params */
			(params) => api.server.apiRaw.update(params),
		onSuccess: (data, params) => {
			invalidateAssets(params.uniq_id);
		},
		onError: snackbarError,
	});
};

/**
 * Updates the hostname (domain) of a server and attempts to update the "favorite" name saved to user/stateData/listforkey
 */
export const useUpdateHostname = () => {
	const queryClient = useQueryClient();

	const { mutate: updateServer, ...rest } = useServerUpdate();
	const { mutate: updateFavorite } = useUpdateFavorite();

	return {
		mutate:
			/**
			 * @param {{domain: string, uniqId: string}} params
			 * @param {{onSuccess: () => void}} options
			 */
			({ domain, uniqId }, { onSuccess }) =>
				updateServer(
					{ domain, uniq_id: uniqId },
					{
						onSuccess: () => {
							onSuccess?.();
							toast.success('Hostname updated successfully.');

							/** @type {import('club-sauce/public/account/user/stateData/index.raw').LWApiPublicAccountUserStateDataListForKeyResultRawI | undefined} */
							const favoritesData = queryClient.getQueryData([
								'/account/user/statedata/listforkey',
								{ key: 'favorites' },
							]);

							// update the saved favorite name if it exists
							const favorite = favoritesData?.data.find(
								(fav) => fav.path === `/servers/${uniqId}`,
							);

							if (favorite) {
								updateFavorite({
									uuid: favorite.uuid,
									path: /** @type {string} */ (favorite.path),
									displayName: domain,
								});
							}
						},
					},
				),
		...rest,
	};
};

export default useServerUpdate;
